import React, { useState } from "react";
import MtaStsValidationResultTable, { IMtaStsValidationTableItem } from "./MtaStsValidationResultTable";
import { IRcaMtaStsValidationMxRecordDetails } from "../../models/IRcaMtaStsValidationMxRecordDetails";
import { IRcaMtaStsValidationStatus } from "../../models/IRcaMtaStsValidationStatus";
import { IRcaMtaStsSectionDetails } from "../../models/IRcaMtaStsSectionDetails";
import { CollapsePanel } from "../CollapsePanel";
import { mergeStyleSets, Stack, StackItem } from "@fluentui/react";
import { MtaStsValidationResultIcon } from "./MtaStsValidationResultIcon";
import { ComponentDecorators } from "../ComponentDecorators";

const classNames = mergeStyleSets({
  root: {
    paddingLeft: 30,
    margin: 15,
  },
});

/**
 * Gets the display for the MX record collapsible header.
 *
 * @param mxRecord the MX record properties
 * @returns the display for MX record collapsible header
 */
const getMxRecordDisplay = (mxRecord: IRcaMtaStsSectionDetails<IRcaMtaStsValidationMxRecordDetails>): React.ReactElement => {
  const shownMxRecordValue: string = mxRecord.Value?.MxValue?.Value || "n/a";
  return (
    <Stack horizontal verticalAlign="center" horizontalAlign="space-around" tokens={{ childrenGap: 8 }}>
      <StackItem>{WebsiteStrings.MtaStsMxRecordLabel}</StackItem>
      <StackItem>{shownMxRecordValue}</StackItem>
      <MtaStsValidationResultIcon mtaStsDetails={mxRecord.Details} mtaStsStatus={mxRecord.Status} />
    </Stack>
  );
};

/**
 * The function to map the received MX record into table items.
 *
 * @param mxRecord  The received MX record to map.
 * @returns The populated table items.
 */
const mapMxRecordToMtaStsValidationTableItem = (mxRecord: IRcaMtaStsValidationMxRecordDetails): IMtaStsValidationTableItem[] => {
  const tableItems: IMtaStsValidationTableItem[] = [];

  tableItems.push({
    Component: "MX Value",
    Value: mxRecord.MxValue?.Value ?? "n/a",
    Status: mxRecord.MxValue?.Status ?? IRcaMtaStsValidationStatus.ConfigurationNotPresent,
    Details: ComponentDecorators.replaceLinks(mxRecord?.MxValue?.Details ?? "n/a"),
  });

  tableItems.push({
    Component: "Host TLS Support",
    Value: mxRecord.TlsSupport?.Value ?? "n/a",
    Status: mxRecord.TlsSupport?.Status ?? IRcaMtaStsValidationStatus.ConfigurationNotPresent,
    Details: ComponentDecorators.replaceLinks(mxRecord?.TlsSupport?.Details ?? "n/a"),
  });
  tableItems.push({
    Component: "Host Certificate",
    Value: mxRecord.Certificate?.Value ?? "n/a",
    Status: mxRecord.Certificate?.Status ?? IRcaMtaStsValidationStatus.ConfigurationNotPresent,
    Details: ComponentDecorators.replaceLinks(mxRecord?.Certificate?.Details ?? "n/a"),
  });
  return tableItems;
};

/**
 * The domain result UI
 *
 * @param props - The component properties
 * @returns The markup
 */
export const MtaStsValidationResultMxRecord: React.FC<{
  /**
   * List of items to render in the table
   */
  mxRecord: IRcaMtaStsSectionDetails<IRcaMtaStsValidationMxRecordDetails>;
}> = ({ mxRecord }) => {
  const [expanded, setExpanded] = useState(true);

  const tableItems = mapMxRecordToMtaStsValidationTableItem(mxRecord.Value);

  return (
    <Stack className={classNames.root}>
      <CollapsePanel
        expanded={expanded}
        renderTitleRow={(): React.ReactElement => getMxRecordDisplay(mxRecord)}
        setExpanded={setExpanded}
        styles={{
          content: {
            root: {
              marginLeft: 20,
            },
          },
        }}
      >
        <MtaStsValidationResultTable items={tableItems} />
      </CollapsePanel>
    </Stack>
  );
};
